import { NavLink, Outlet, useLocation } from "react-router-dom";

/**
 * AnalyticsPage - Analytics dashboard layout with sub-navigation.
 *
 * Contains tabs for:
 * - Engagement analytics
 * - Progress analytics
 * - Conversion analytics
 */
export function AnalyticsPage() {
  const location = useLocation();
  const isIndex = location.pathname === "/admin/analytics";

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        <p className="mt-1 text-sm text-gray-500">
          Insights into platform usage and performance
        </p>
      </div>

      {/* Sub-navigation tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6" aria-label="Analytics tabs">
          <TabLink to="/admin/analytics" end>
            Engagement
          </TabLink>
          <TabLink to="/admin/analytics/progress">
            Progress
          </TabLink>
          <TabLink to="/admin/analytics/conversions">
            Conversions
          </TabLink>
        </nav>
      </div>

      {/* Content */}
      {isIndex ? <EngagementAnalytics /> : <Outlet />}
    </div>
  );
}

interface TabLinkProps {
  to: string;
  children: React.ReactNode;
  end?: boolean;
}

function TabLink({ to, children, end }: TabLinkProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => `
        pb-3 text-sm font-medium border-b-2 -mb-px transition-colors
        ${
          isActive
            ? "text-magic-600 border-magic-600"
            : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
        }
      `}
    >
      {children}
    </NavLink>
  );
}

/**
 * Default engagement analytics content
 */
export function EngagementAnalytics() {
  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard
          title="Daily Active Users"
          value="—"
          subtitle="Last 7 days average"
        />
        <MetricCard
          title="Avg. Session Duration"
          value="—"
          subtitle="Minutes per session"
        />
        <MetricCard
          title="Challenges Completed"
          value="—"
          subtitle="This month"
        />
      </div>

      {/* Chart placeholder */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          User Activity Over Time
        </h2>
        <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg">
          <p className="text-sm text-gray-500">Chart coming soon...</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Progress analytics content
 */
export function ProgressAnalytics() {
  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard
          title="Avg. Stars Earned"
          value="—"
          subtitle="Per child"
        />
        <MetricCard
          title="Badges Earned"
          value="—"
          subtitle="Total this month"
        />
        <MetricCard
          title="Levels Completed"
          value="—"
          subtitle="Total this month"
        />
      </div>

      {/* Chart placeholder */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Progress by Discipline
        </h2>
        <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg">
          <p className="text-sm text-gray-500">Chart coming soon...</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Conversion analytics content
 */
export function ConversionAnalytics() {
  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard
          title="Free to Paid Rate"
          value="—"
          subtitle="Conversion percentage"
        />
        <MetricCard
          title="Churn Rate"
          value="—"
          subtitle="Monthly average"
        />
        <MetricCard
          title="MRR"
          value="—"
          subtitle="Monthly recurring revenue"
        />
      </div>

      {/* Chart placeholder */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Subscription Funnel
        </h2>
        <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg">
          <p className="text-sm text-gray-500">Chart coming soon...</p>
        </div>
      </div>
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string;
  subtitle: string;
}

function MetricCard({ title, value, subtitle }: MetricCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <p className="text-sm font-medium text-gray-500">{title}</p>
      <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
      <p className="mt-1 text-xs text-gray-400">{subtitle}</p>
    </div>
  );
}

export default AnalyticsPage;
